import { useMemo } from "react";
import { useAppSelector } from "../../app";
import { ButtonType } from "../../common";
import { Status } from "../../common/status/status";
import { useGetHoldReasonsQuery } from "../../documentTypes/store/documentTypeApi";

// Without hold reasons configured - the default - nothing is fetched and the hold flow
// is unchanged.
const useHoldReasonPicker = (buttonType: ButtonType, status: Status) => {
  const documentType = useAppSelector(
    (state) => state.documentReducer.activeDocument?.documentType,
  );
  const activeDocumentSet = useAppSelector(
    (state) => state.documentSetsReducer.activeDocumentSet,
  );

  // On an error status the hold button keeps that status instead of holding, so there is
  // nothing to give a reason for.
  const holds =
    buttonType === ButtonType.Hold &&
    (status === Status.OnHold || status === Status.AnnotationOnHold);

  const isConfigured = holds && !!documentType?.holdReasonsEnabled;
  const inputId = activeDocumentSet?.input?.id;

  const { data, isFetching, refetch } = useGetHoldReasonsQuery(
    { documentTypeId: documentType?.id ?? "", inputId: inputId ?? "" },
    { skip: !isConfigured || !documentType?.id || !inputId },
  );

  const reasons = useMemo(() => data ?? [], [data]);

  return {
    // Still asking while the list is in flight, otherwise a fast click slips past the picker.
    showPicker: isConfigured && (isFetching || reasons.length > 0),
    isConfigured,
    isLoading: isFetching,
    isRequired: isConfigured && !!documentType?.holdReasonRequired,
    reasons,
    currentReason: activeDocumentSet?.holdReason,
    // The settings page lives in another api slice, so linking a reason there cannot
    // invalidate this list.
    refetch,
  };
};

export default useHoldReasonPicker;
